import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, AlertTriangle, ArrowUpRight } from "lucide-react";
import BrandMark from "@/components/landing/BrandMark";

const APP_URL = "https://app.streeteatsrewards.com";
const MAX_POLLS = 6;

export default function PaymentSuccess() {
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const poll = useCallback(
    async (attempt) => {
      if (!mountedRef.current) return;
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!mountedRef.current) return;
        if (data.status === "completed" || data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
      } catch {
        // keep retrying
      }
      timerRef.current = setTimeout(() => poll(attempt + 1), 1500);
    },
    [sessionId]
  );

  useEffect(() => {
    mountedRef.current = true;
    poll(0);
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [poll]);

  return (
    <main className="min-h-screen bg-[#07182D] text-white grid place-items-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-20" aria-hidden />
      <div className="absolute w-[440px] h-[440px] rounded-full bg-[#D4A017]/10 blur-[130px]" aria-hidden />

      <div className="relative z-10 max-w-lg w-full text-center border-2 border-white/10 bg-[#0B1F3A] p-10">
        <div className="flex justify-center mb-6">
          <BrandMark size={56} />
        </div>

        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={40} className="text-[#D4A017] animate-spin mx-auto" />
            <h1 className="font-display uppercase text-3xl mt-5">Confirming your plan…</h1>
            <p className="text-white/70 text-sm mt-3">Hang tight while we set up your account.</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="payment-success">
            <CheckCircle2 size={44} className="text-[#2D7A4F] mx-auto" />
            <h1 className="font-display uppercase text-4xl mt-5 leading-none">You&rsquo;re in.</h1>
            <p className="text-white/75 text-base mt-4 leading-relaxed">
              Your 14-day free trial has started — you won&rsquo;t be charged until it
              ends, and you can cancel anytime. Let&rsquo;s build your first loyalty card.
            </p>
            <a
              href={APP_URL}
              data-testid="payment-continue"
              className="btn-brutal mt-8 inline-flex items-center justify-center gap-2 bg-[#D4A017] text-[#07182D] font-display uppercase tracking-wide px-7 py-4 border-2 border-[#D4A017] shadow-hard-sm"
            >
              Go to your dashboard
              <ArrowUpRight size={18} strokeWidth={2.5} />
            </a>
          </div>
        )}

        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-pending">
            <AlertTriangle size={40} className="text-[#F59E0B] mx-auto" />
            <h1 className="font-display uppercase text-3xl mt-5">Almost there</h1>
            <p className="text-white/75 text-sm mt-3 leading-relaxed">
              {state === "timeout"
                ? "Your payment is still processing. If you completed checkout, your trial will activate shortly — check your email for confirmation."
                : "We couldn't confirm this session. If you were charged, contact support and we'll sort it out right away."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/"
                className="btn-brutal inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-white/40 hover:border-[#D4A017] hover:text-[#D4A017] font-display uppercase tracking-wide px-6 py-3.5"
              >
                Back to site
              </a>
              <a
                href="mailto:Join@streeteatsrewards.com"
                className="font-mono text-xs uppercase tracking-widest text-white/60 hover:text-white"
              >
                Contact support
              </a>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
